import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import SuggestHerbCallout from "../../components/shared/SuggestHerbCallout";
import {
  ALL_INGREDIENTS,
  INGREDIENT_LIBRARY_FILTERS,
  ingredientMatchesLibraryFilter,
  getCategorySignpostIcon,
  INGREDIENT_IMAGE_FALLBACK,
} from "../../lib/ingredients";

function matchesSearch(ing, needle) {
  if (!needle) return true;
  const blob = [ing.name, ing.flavour, ing.shortDescription, ing.category, ...(ing.routineTags || [])]
    .join(" ")
    .toLowerCase();
  return blob.includes(needle);
}

function IngredientCard({ ing }) {
  const CatIco = getCategorySignpostIcon(ing);
  return (
    <Link
      to={`/ingredients/${ing.slug}`}
      className="group rounded-3xl border border-cream bg-white overflow-hidden shadow-soft hover:shadow-hover transition flex flex-col"
    >
      <div className="aspect-[16/10] bg-cream overflow-hidden relative">
        <img
          src={ing.image || INGREDIENT_IMAGE_FALLBACK}
          alt={ing.imageAlt || `${ing.name} ingredient image`}
          className="w-full h-full object-cover editorial-img transition duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute top-3 left-3 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
            ing.blendAvailable ? "bg-gold/90 text-forest" : "bg-white/90 text-botanical/80"
          }`}
        >
          {ing.blendAvailable ? "Common mix-in idea" : "Learn only"}
        </span>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-gold">
          <CatIco className="w-3.5 h-3.5 text-gold/90 shrink-0" strokeWidth={1.65} aria-hidden />
          {ing.category}
        </span>
        <h2 className="font-serif text-xl text-forest mt-2 group-hover:text-botanical transition">{ing.name}</h2>
        <p className="mt-2 text-sm text-botanical/80 leading-relaxed line-clamp-3 flex-1">{ing.shortDescription}</p>
        <span className="mt-4 text-xs tracking-widest uppercase text-forest/60 group-hover:text-forest">Learn more →</span>
      </div>
    </Link>
  );
}

export default function IngredientsIndexPage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [libraryOpen, setLibraryOpen] = useState(false);

  const needle = q.trim().toLowerCase();

  const launchHerbs = useMemo(() => {
    return ALL_INGREDIENTS.filter((row) => {
      if (!row.blendAvailable) return false;
      if (!ingredientMatchesLibraryFilter(row, cat)) return false;
      return matchesSearch(row, needle);
    });
  }, [needle, cat]);

  const libraryHerbs = useMemo(() => {
    return ALL_INGREDIENTS.filter((row) => {
      if (!row.libraryOnly) return false;
      if (!ingredientMatchesLibraryFilter(row, cat)) return false;
      return matchesSearch(row, needle);
    });
  }, [needle, cat]);

  const showLaunch = cat !== "learn";
  const showLibrary = cat !== "blend";

  useEffect(() => {
    if (cat === "learn") setLibraryOpen(true);
  }, [cat]);

  return (
    <SiteShell theme="dark">
      <SEO
        title="Herb and ingredient library | Moss Matrix"
        description="Explore Moss Matrix sea moss gel ingredients and mix-in ideas people commonly research. Routine-based education and careful wellness language."
        path="/ingredients"
        ogType="website"
      />
      <ContentPageLayout
        eyebrow="Ingredients"
        title="A calm library of herbs & powders."
        subtitle="Routine-based education, not symptom picking. Learn what people commonly pair with sea moss — then start with plain Gold or Purple."
      >
        <p className="text-sm text-botanical/85 leading-relaxed max-w-3xl mb-4 -mt-4">
          Some herbs are educational library entries or future options. Custom blends are being simplified so we can keep orders fresh,
          consistent, and manageable.
        </p>
        <p className="text-sm text-botanical/85 leading-relaxed max-w-3xl mb-8">
          Selected mix-ins may return later. The wider library is here for learning and future product development.
        </p>

        <div className="mb-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="relative max-w-md w-full">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search ingredients…"
              className="w-full rounded-full border border-cream bg-white px-5 py-2.5 text-sm text-forest placeholder:text-botanical/45"
            />
          </div>
          <Link
            to="/blends"
            className="text-xs tracking-widest uppercase text-forest underline hover:text-gold whitespace-nowrap"
          >
            Curated blend routines →
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {INGREDIENT_LIBRARY_FILTERS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCat(c.id)}
              className={`rounded-full px-3 py-1.5 text-[11px] tracking-wide uppercase transition ${
                cat === c.id ? "bg-forest text-cream" : "bg-cream text-forest hover:bg-gold/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="rounded-[2rem] overflow-hidden border border-cream bg-cream/30 aspect-[4/3] md:aspect-auto md:min-h-[280px]">
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/ingredients-spoon-texture.png`}
              alt="Sea moss gel on a wooden spoon, macro texture"
              className="w-full h-full object-cover editorial-img"
            />
          </div>
          <div className="rounded-[2rem] overflow-hidden border border-cream bg-cream/30 aspect-[4/3] md:aspect-auto md:min-h-[280px]">
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/ingredients-jars-botanicals.png`}
              alt="Moss Matrix sea moss gel jar with raw sea moss and powdered herbs"
              className="w-full h-full object-cover editorial-img"
            />
          </div>
        </div>

        {showLaunch ? (
          <section className="mb-12" aria-labelledby="launch-herbs-heading">
            <h2 id="launch-herbs-heading" className="font-serif text-2xl text-forest mb-2">
              Featured mix-in ideas
            </h2>
            <p className="text-sm text-botanical/75 mb-6 max-w-2xl">
              Learn what people commonly pair with sea moss. Start with plain Gold or Purple — selected mix-ins may return later.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {launchHerbs.map((ing) => (
                <IngredientCard key={ing.id} ing={ing} />
              ))}
            </div>
            {launchHerbs.length === 0 && (
              <p className="text-center text-botanical/70 py-8">No launch herbs match this search or filter.</p>
            )}
          </section>
        ) : null}

        {showLibrary ? (
          <section className="mb-12" aria-labelledby="full-library-heading">
            <button
              type="button"
              id="full-library-heading"
              onClick={() => setLibraryOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-4 rounded-2xl border border-cream bg-white px-5 py-4 text-left hover:border-gold/40 transition"
              aria-expanded={libraryOpen}
            >
              <span>
                <span className="font-serif text-xl text-forest block">Full herb library</span>
                <span className="text-sm text-botanical/70 mt-1 block">
                  Learn-only and future options — educational reading, not checkout add-ons.
                </span>
              </span>
              <ChevronDown
                className={`w-5 h-5 text-forest shrink-0 transition-transform ${libraryOpen ? "rotate-180" : ""}`}
                strokeWidth={1.8}
                aria-hidden
              />
            </button>
            {libraryOpen ? (
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {libraryHerbs.map((ing) => (
                  <IngredientCard key={ing.id} ing={ing} />
                ))}
                {libraryHerbs.length === 0 && (
                  <p className="col-span-full text-center text-botanical/70 py-8">No library entries match this search or filter.</p>
                )}
              </div>
            ) : null}
          </section>
        ) : null}

        <SuggestHerbCallout className="mb-12 max-w-xl" />

        <div className="max-w-2xl mx-auto space-y-6">
          <WellnessDisclaimer />
          <div className="text-center">
            <Link
              to="/mix-ins"
              className="inline-flex rounded-full bg-forest text-cream px-8 py-3 text-sm font-medium hover:bg-botanical transition shadow-soft"
            >
              Explore mix-in ideas
            </Link>
          </div>
        </div>
      </ContentPageLayout>
    </SiteShell>
  );
}
